import { ApiService } from "./api.service";
import { API_BASE_URL } from "@/config";
import { markPendingAuthEvent } from "@/lib/auth-analytics";
import { markPageLeaving } from "@/lib/leave-page";

interface EmailCheckResponse {
  status: "MAGIC_CODE" | "CREDENTIAL" | "SSO" | "SSO_UNAVAILABLE";
  existing: boolean;
  is_password_autoset: boolean;
}

export class AuthService extends ApiService {
  constructor() {
    super(API_BASE_URL);
  }

  async requestCSRFToken(): Promise<string> {
    const data = await this.get<{ csrf_token: string }>("/auth/get-csrf-token/");
    return data.csrf_token;
  }

  async emailCheck(email: string): Promise<EmailCheckResponse> {
    return this.post<EmailCheckResponse>("/auth/email-check/", { email });
  }

  /** Asks whether sign-up would accept `password` for this invited email. The
   *  server answers only for the invitation the link carries, so `invitationId`
   *  is required. A refusal rejects with a 400 whose body carries the
   *  validators' `codes` (read it with `refusedPasswordKeys`). */
  async checkSignUpPassword(
    invitationId: string,
    email: string,
    password: string,
    names: { firstName: string; lastName: string },
  ): Promise<{ valid: true }> {
    return this.post("/auth/sign-up/password-check/", {
      invitation_id: invitationId,
      email,
      password,
      first_name: names.firstName,
      last_name: names.lastName,
    });
  }

  async magicGenerate(email: string): Promise<{ code?: string }> {
    return this.post("/auth/magic-generate/", { email });
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.post("/auth/forgot-password/", { email });
  }

  async setPassword(password: string): Promise<{ message: string }> {
    const csrfToken = await this.requestCSRFToken();
    return this.post(
      "/auth/set-password/",
      { password },
      { headers: { "X-CSRFTOKEN": csrfToken } },
    );
  }

  async changePassword(
    oldPassword: string | undefined,
    newPassword: string,
  ): Promise<{ message: string }> {
    const csrfToken = await this.requestCSRFToken();
    return this.post(
      "/auth/change-password/",
      { old_password: oldPassword, new_password: newPassword },
      { headers: { "X-CSRFTOKEN": csrfToken } },
    );
  }

  async signIn(email: string, password: string, nextPath?: string): Promise<void> {
    const csrfToken = await this.requestCSRFToken();
    markPendingAuthEvent("signed_in", "password");
    this.submitForm("/auth/sign-in/", {
      csrfmiddlewaretoken: csrfToken,
      email,
      password,
      ...(nextPath && { next_path: nextPath }),
    });
  }

  async signUp(
    email: string,
    password: string,
    confirmPassword: string,
    options?: { firstName?: string; lastName?: string; nextPath?: string },
  ): Promise<void> {
    const csrfToken = await this.requestCSRFToken();
    markPendingAuthEvent("signed_up", "password");
    this.submitForm("/auth/sign-up/", {
      csrfmiddlewaretoken: csrfToken,
      email,
      password,
      confirm_password: confirmPassword,
      ...(options?.firstName && { first_name: options.firstName }),
      ...(options?.lastName && { last_name: options.lastName }),
      ...(options?.nextPath && { next_path: options.nextPath }),
    });
  }

  /** Submits the sign-out form, whose response replaces the whole app.
   *
   *  Rejects if the CSRF token can't be fetched; once the form is submitted it
   *  never settles, so no caller can navigate, reset state or load anything
   *  after it: in Safari that abandons the sign-out (NG-2513, `lib/leave-page`). */
  async signOut(): Promise<never> {
    const csrfToken = await this.requestCSRFToken();
    this.submitForm("/auth/sign-out/", {
      csrfmiddlewaretoken: csrfToken,
    });
    return new Promise<never>(() => {});
  }

  ssoInitiate(email: string, nextPath?: string): void {
    markPendingAuthEvent("signed_in", "sso");
    this.submitForm("/auth/sso/", {
      email,
      ...(nextPath && { next_path: nextPath }),
    });
  }

  async magicSignIn(email: string, code: string, nextPath?: string): Promise<void> {
    const csrfToken = await this.requestCSRFToken();
    markPendingAuthEvent("signed_in", "magic_code");
    this.submitForm("/auth/magic-sign-in/", {
      csrfmiddlewaretoken: csrfToken,
      email,
      code,
      ...(nextPath && { next_path: nextPath }),
    });
  }

  async magicSignUp(email: string, code: string): Promise<void> {
    const csrfToken = await this.requestCSRFToken();
    markPendingAuthEvent("signed_up", "magic_code");
    this.submitForm("/auth/magic-sign-up/", {
      csrfmiddlewaretoken: csrfToken,
      email,
      code,
    });
  }

  async resetPassword(
    uidb64: string,
    token: string,
    password: string,
    confirmPassword: string,
  ): Promise<void> {
    const csrfToken = await this.requestCSRFToken();
    this.submitForm(`/auth/reset-password/${uidb64}/${token}/`, {
      csrfmiddlewaretoken: csrfToken,
      password,
      confirm_password: confirmPassword,
    });
  }

  /** Submits a hidden form: a full page load, so the page is marked as leaving
   *  first and nothing may navigate it while the request is pending. */
  private submitForm(path: string, fields: Record<string, string>): void {
    markPageLeaving();
    const form = document.createElement("form");
    form.method = "POST";
    form.action = `${this.baseURL}${path}`;
    form.style.display = "none";

    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
  }
}
